// new ui 
import { takeLoginNotice } from "../CustomHooks/serverPush";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { login, logout } from "../Store/Slices/AuthSlice";
import playstore from "../Assets/Images/playstore.png";
// import bingopayLogo from "../Assets/Images/logo_png_with_shreebalajee (1).png";
import bingopayLogo from "../Assets/Images/logo_png_with_shreebalajee (1).png";
import useApi from "../CustomHooks/useApi";
import useGeoLocation from "../CustomHooks/useGeoLocation";
import { fullTime, shortSessionId } from "../CustomHooks/sessionLabels";
import {
  verifyDevice, resendDeviceOtp, describeDeviceError, setDeviceMpin as saveNewMpin, confirmDeviceMpinOtp,
  nextStepAfter, removeTrustedDevice, endLoginSession,
} from "../CustomHooks/deviceVerify";
import { 
  Eye, 
  EyeOff, 
  Smartphone, 
  Lock, 
  KeyRound, 
  ShieldCheck, 
  Zap, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight, 
  ArrowLeft,
  Loader2,
  Sparkles,
  Check
} from "lucide-react";

function AuthContainer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fetchLocation } = useGeoLocation();

  // View State: 'LOGIN' | 'DEVICE' (device check / MPIN setup / MPIN confirm) | 'OTP' | 'RESET_PASSWORD' | 'RESET_MPIN'
  const [view, setView] = useState("LOGIN");

  // Input States
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mpin, setMpin] = useState("");
  const [confirmMpin, setConfirmMpin] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI & Animation States
  const [errorMessage, setErrorMessage] = useState(takeLoginNotice());      // e.g. "Your session was ended" after an admin ended it
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessAnimating, setIsSuccessAnimating] = useState(false);
  const [authSuccessDetails, setAuthSuccessDetails] = useState({ name: "", targetRoute: "" });
  const [pendingResetPayload, setPendingResetPayload] = useState(null);
  const [otpSentTo, setOtpSentTo] = useState("");

  // Device check (the gateway answered the sign-in with GWD001 / GWD010 / GWD012 instead of the login)
  const [deviceChallenge, setDeviceChallenge] = useState(null); // { challengeId, otpTtlSeconds, maskedMobile, mpinOnly, stage }
  const [deviceOtp, setDeviceOtp] = useState("");
  const [deviceMpin, setDeviceMpin] = useState("");
  const [deviceMpinConfirm, setDeviceMpinConfirm] = useState("");
  const [noticeMessage, setNoticeMessage] = useState("");      // green message between the device steps
  const [resendIn, setResendIn] = useState(0);
  const [pickedChoice, setPickedChoice] = useState(null);      // the device / session the user picked on a choose screen

  // APIs
  const loginApi = useApi("/staff/onboard/signin", "POST", false);
  const resetApi = useApi("/staff/onboard/forget/password", "POST", false);

  useEffect(() => {
    dispatch(logout());
  }, [dispatch]);

  // Counts the "Resend OTP" wait down to 0 (the gateway enforces the same 60 s cooldown)
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn((prev) => Math.max(prev - 1, 0)), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendIn > 0]);

  // Ask for the browser location as soon as the login page opens, so it is already known when "Sign In" is
  // pressed — the gateway's audit trail records it with the sign-in (X-Latitude / X-Longitude, see
  // CustomHooks/auditHeaders.js). Optional: denied or unsupported just means no location is recorded.
  useEffect(() => {
    fetchLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Triggers interactive welcome screen before redirect
  const triggerWelcomeTransition = (targetRoute, partnerName) => {
    setAuthSuccessDetails({ name: partnerName, targetRoute });
    setIsSuccessAnimating(true);

    setTimeout(() => {
      navigate(targetRoute);
    }, 1800);
  };

  // What happens once the backend has accepted the sign-in (also used after a new device passed its checks)
  const finishLogin = (data) => {
    if (data) {
      const isEkycCompleted = Boolean(data.outlet_id);

      dispatch(
        login({
          ...data,
          isEkycCompleted,
        })
      );

      localStorage.setItem("loginTime", Date.now().toString());
      localStorage.setItem("isEkycCompleted", isEkycCompleted ? "true" : "false");
      localStorage.setItem("isFirstLogin", "false");

      if (data.error_code === "DMT022") {
        setView("RESET_PASSWORD");
      } else if (data.error_code === "DMT023") {
        setErrorMessage(data.message);
      } else if (data.error_code === 202) {
        setErrorMessage(data.message);
      } else if (data.role === "MASTER_DISTRIBUTOR") {
        triggerWelcomeTransition("/masterdistributordashboard", data?.others?.merchant_shop_name || "Master Distributor");
      } else if (data.role === "DISTRIBUTOR") {
        triggerWelcomeTransition("/distributordashboard", data?.others?.merchant_shop_name || "Distributor");
      } else if (
        data.role === "MERCHANT" ||
        data.role === "INDEPENDENT_MERCHANT"
      ) {
        triggerWelcomeTransition("/retailerdashboard", data?.others?.merchant_shop_name || "Merchant Partner");
      } else {
        setErrorMessage("Invalid Role configured for this account.");
      }
    } else {
      setErrorMessage("Invalid credentials or server error.");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!mobile || mobile.length !== 10) {
      setErrorMessage("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (!password) {
      setErrorMessage("Please enter your password.");
      return;
    }

    setIsLoading(true);

    try {
      const body = {
        mobile_number: mobile,
        password: password,
      };

      const data = await loginApi.callApi(body);

      if (data?.code === "GWD001") {
        startDeviceVerification(data);
        return;
      }
      if (data?.code === "GWD010") {      // known device: no OTP, the MPIN is still asked for
        startDeviceVerification({ ...data, mpinOnly: true });
        return;
      }
      if (data?.code === "GWD012") {      // known device, but the user has no MPIN yet: set one first
        startDeviceVerification({ ...data, mpinOnly: true, stage: "MPIN_SETUP" });
        return;
      }

      finishLogin(data);
    } catch (error) {
      console.error("Login Error:", error);
      const gw = error?.response?.data;
      if (typeof gw?.code === "string" && (gw.code.startsWith("GWD") || gw.code === "GWB004") && gw.message) {
        setErrorMessage(gw.message);   // e.g. the OTP SMS could not be sent, or too many active sessions
      } else {
        setErrorMessage("An unexpected error occurred. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Device check, MPIN setup and MPIN confirmation ────────────────────────────────────────────────────
  // stage: DEVICE_OTP       new device, user has an MPIN: OTP + MPIN together
  //        DEVICE_OTP_ONLY  new device, no MPIN yet: the OTP alone
  //        MPIN_SETUP       "MPIN setup required": choose an MPIN and confirm it
  //        SETUP_OTP        the OTP sent after that, which saves the MPIN
  //        MPIN_CONFIRM     the MPIN once more (a known device asks only this), then the dashboard
  //        CHOOSE_DEVICE    the device list is full: pick a device to stop trusting (after the OTP and the MPIN)
  //        CHOOSE_SESSION   the sessions are full: pick a session to log out (after the MPIN)
  const MPIN_SETUP_NOTICE = "MPIN setup required. Please set your MPIN.";

  const startDeviceVerification = (data) => {
    const stage = data.stage || (data.mpinOnly ? "MPIN_CONFIRM" : data.mpinSetup ? "DEVICE_OTP_ONLY" : "DEVICE_OTP");
    setDeviceChallenge({
      challengeId: data.challengeId,
      otpTtlSeconds: data.otpTtlSeconds,
      maskedMobile: data.maskedMobile,
      mpinOnly: !!data.mpinOnly,
      stage,
    });
    setDeviceOtp("");
    setDeviceMpin("");
    setDeviceMpinConfirm("");
    setResendIn(stage === "DEVICE_OTP" || stage === "DEVICE_OTP_ONLY" ? 60 : 0);
    setNoticeMessage(stage === "MPIN_SETUP" ? MPIN_SETUP_NOTICE : "");
    setErrorMessage("");
    setView("DEVICE");
  };

  const moveToStage = (stage, patch = {}) => setDeviceChallenge((c) => ({ ...c, ...patch, stage }));

  const leaveDeviceVerification = (message) => {
    setDeviceChallenge(null);
    setDeviceOtp("");
    setDeviceMpin("");
    setDeviceMpinConfirm("");
    setPickedChoice(null);
    setNoticeMessage("");
    setPassword("");
    setView("LOGIN");
    setErrorMessage(message || "");
  };

  // A failed call: back to the login form for the errors only a new login fixes, otherwise show it and stay.
  const handleDeviceError = (error, { keepOtp = false } = {}) => {
    const outcome = describeDeviceError(error);
    if (outcome.action === "restart") {
      leaveDeviceVerification(outcome.message);
      return;
    }
    setErrorMessage(outcome.message);
    setDeviceMpin("");
    setDeviceMpinConfirm("");
    if (!keepOtp && outcome.message !== "Invalid MPIN.") setDeviceOtp("");   // a wrong MPIN keeps the OTP, which is still valid
  };

  // Whatever the gateway answered to a verify / remove / end-session call: the next step, a choice to make, or the login.
  const settleDeviceStep = (res) => {
    const step = nextStepAfter(res);
    if (step.problem) {
      leaveDeviceVerification(step.problem);
      return;
    }
    if (step.stage) {
      moveToStage(step.stage, step.patch);
      setDeviceOtp("");
      setDeviceMpin("");
      setPickedChoice(null);
      setNoticeMessage(step.notice || "");
      return;
    }
    finishLogin(step.signin);      // the device secret was already remembered by the call; it must not end up in the login state
  };

  const handleDeviceVerify = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    const stage = deviceChallenge.stage;
    if (stage !== "MPIN_CONFIRM" && deviceOtp.length !== 6) {
      setErrorMessage("Enter the 6-digit OTP.");
      return;
    }
    if (stage !== "DEVICE_OTP_ONLY" && deviceMpin.length !== 6) {
      setErrorMessage("Enter your 6-digit MPIN.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await verifyDevice({
        challengeId: deviceChallenge.challengeId,
        otp: stage === "MPIN_CONFIRM" ? undefined : deviceOtp,
        mpin: stage === "DEVICE_OTP_ONLY" ? undefined : deviceMpin,
      });
      settleDeviceStep(res);
    } catch (error) {
      handleDeviceError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMpinSetup = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (deviceMpin.length !== 6) {
      setErrorMessage("Set a 6-digit MPIN.");
      return;
    }
    if (deviceMpin !== deviceMpinConfirm) {
      setErrorMessage("MPIN and confirm MPIN do not match.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await saveNewMpin({
        challengeId: deviceChallenge.challengeId,
        mpin: deviceMpin,
        confirmMpin: deviceMpinConfirm,
      });
      moveToStage("SETUP_OTP", { maskedMobile: res?.maskedMobile });
      setDeviceMpin("");
      setDeviceMpinConfirm("");
      setDeviceOtp("");
      setResendIn(60);
      setNoticeMessage(`We sent a 6-digit OTP to ${res?.maskedMobile || "your mobile"}. Enter it to confirm your new MPIN.`);
    } catch (error) {
      handleDeviceError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupOtp = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    if (deviceOtp.length !== 6) {
      setErrorMessage("Enter the 6-digit OTP.");
      return;
    }
    setIsLoading(true);
    try {
      await confirmDeviceMpinOtp({ challengeId: deviceChallenge.challengeId, otp: deviceOtp });
      moveToStage("MPIN_CONFIRM");
      setDeviceOtp("");
      setDeviceMpin("");
      setNoticeMessage("Your MPIN has been set successfully. Enter your MPIN to continue.");
    } catch (error) {
      handleDeviceError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // The user's choice when the device list (CHOOSE_DEVICE) or the sessions (CHOOSE_SESSION) are full.
  const handleChoose = async () => {
    if (pickedChoice === null || !deviceChallenge) return;
    setErrorMessage("");
    setIsLoading(true);
    try {
      const res = deviceChallenge.stage === "CHOOSE_DEVICE"
        ? await removeTrustedDevice({ challengeId: deviceChallenge.challengeId, deviceId: pickedChoice })
        : await endLoginSession({ challengeId: deviceChallenge.challengeId, sessionId: pickedChoice });
      settleDeviceStep(res);
    } catch (error) {
      handleDeviceError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeviceResend = async () => {
    if (resendIn > 0 || !deviceChallenge) return;
    setErrorMessage("");
    try {
      await resendDeviceOtp({ challengeId: deviceChallenge.challengeId });
      setResendIn(60);
      setDeviceOtp("");            // the new SMS replaces the old OTP: clear what was typed
    } catch (error) {
      const outcome = describeDeviceError(error);
      if (outcome.action === "restart") {
        leaveDeviceVerification(outcome.message);
      } else {
        setErrorMessage(outcome.message);
        setResendIn(60);
      }
    }
  };

  const triggerForgotPassword = () => {
    setErrorMessage("");
    if (!mobile || mobile.length !== 10) {
      setErrorMessage("Please enter a valid 10-digit mobile number first.");
      return;
    }
    setNewPassword("");
    setConfirmPassword("");
    setOtp("");
    setPendingResetPayload(null);
    setOtpSentTo("");
    setView("RESET_PASSWORD");
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!otp || otp.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit OTP.");
      return;
    }

    setIsLoading(true);

    try {
      const res = await resetApi.callApi({ ...pendingResetPayload, otp });
      if (res?.status === "VERIFIED" || res?.status === "SUCCESS") {
        setPendingResetPayload(null);
        setOtpSentTo("");
        if (pendingResetPayload?.password !== undefined) {
          setView("RESET_MPIN");
        } else {
          window.location.reload();
        }
        setErrorMessage("");
      } else {
        setErrorMessage(res?.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "OTP verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (view === "RESET_PASSWORD") {
      if (!newPassword || !confirmPassword) {
        setErrorMessage("Please fill all password fields.");
        return;
      }
      if (newPassword !== confirmPassword) {
        setErrorMessage("Passwords do not match.");
        return;
      }

      setIsLoading(true);
      const payload = { mobile_number: mobile, password: newPassword, confirm_password: confirmPassword };

      try {
        const res = await resetApi.callApi(payload);
        if (res?.status === "VERIFIED" || res?.status === "SUCCESS") {
          setView("RESET_MPIN");
          setErrorMessage("");
        } else {
          setErrorMessage(res?.message || "Password reset failed.");
        }
      } catch (err) {
        if (err?.response?.status === 400 && err?.response?.data?.error_code === "OTP_REQUIRED") {
          setPendingResetPayload(payload);
          setOtpSentTo(err?.response?.data?.action_params?.sentTo || "");
          setOtp("");
          setView("OTP");
        } else {
          setErrorMessage(err?.response?.data?.message || "Password reset failed.");
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      if (!mpin || !confirmMpin) {
        setErrorMessage("Please fill all MPIN fields.");
        return;
      }
      if (mpin.length !== 6 || confirmMpin.length !== 6) {
        setErrorMessage("MPIN must be exactly 6 digits.");
        return;
      }
      if (mpin !== confirmMpin) {
        setErrorMessage("MPIN values do not match.");
        return;
      }

      setIsLoading(true);
      const payload = { mobile_number: mobile, mpin, confirm_mpin : confirmMpin };

      try {
        const res = await resetApi.callApi(payload);
        if (res?.status === "VERIFIED" || res?.status === "SUCCESS") {
          window.location.reload();
        } else {
          setErrorMessage(res?.message || "MPIN reset failed.");
        }
      } catch (err) {
        if (err?.response?.status === 400 && err?.response?.data?.error_code === "OTP_REQUIRED") {
          setPendingResetPayload(payload);
          setOtpSentTo(err?.response?.data?.action_params?.sentTo || "");
          setOtp("");
          setView("OTP");
        } else {
          setErrorMessage(err?.response?.data?.message || "MPIN reset failed.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 sm:p-6 lg:p-8 font-sans selection:bg-blue-500 selection:text-white overflow-hidden">
      
      {/* ================= POST-LOGIN SUCCESS WELCOME OVERLAY ================= */}
      {isSuccessAnimating && (
        <div className="fixed inset-0 z-[9999] bg-[#0A2540]/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="relative bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-white/20 text-center flex flex-col items-center animate-in zoom-in-95 duration-300">
            {/* Animated Checkmark Halo */}
            <div className="relative mb-5">
              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-amber-400 to-yellow-500 animate-ping opacity-25 absolute inset-0" />
              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-[#0A2540] to-[#123E6B] text-white flex items-center justify-center shadow-xl relative border-4 border-white">
                <Check className="w-10 h-10 text-amber-400 stroke-[3] animate-in zoom-in duration-300" />
              </div>
            </div>

            <div className="space-y-1 mb-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" /> Authorized Session
              </div>
              <h2 className="text-xl font-extrabold text-slate-800 tracking-tight">
                Welcome Back!
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                {authSuccessDetails.name}
              </p>
            </div>

            <div className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-slate-50 border border-slate-200/70 text-xs font-semibold text-slate-600">
              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
              <span>Launching terminal dashboard...</span>
            </div>
          </div>
        </div>
      )}

      {/* ================= MAIN CARD CONTAINER ================= */}
      <div className="w-full max-w-5xl bg-white rounded-3xl shadow-xl shadow-slate-900/5 border border-slate-200/80 overflow-hidden grid grid-cols-1 lg:grid-cols-12 min-h-[620px]">
        
        {/* ================= LEFT SECTION: BRANDING BANNER ================= */}
        <div className="hidden lg:flex lg:col-span-5 bg-gradient-to-br from-[#0A2540] via-[#0F3057] to-[#123E6B] p-10 flex-col justify-between relative overflow-hidden text-white">
          <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 space-y-8">
            <div className="bg-white/90 backdrop-blur-md border border-white/15 p-3 rounded-2xl inline-block shadow-xs">
              <img src={bingopayLogo} alt="" className="h-10 w-auto object-contain " />
            </div>

            <div className="space-y-3">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-white/10 text-blue-200 border border-white/15 backdrop-blur-xs">
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                Next-Gen Retailer Terminal
              </div>
              <h1 className="text-3xl font-extrabold tracking-tight leading-tight text-white">
                Fast, Secure & Intelligent Payments.
              </h1>
              <p className="text-xs text-blue-100/80 leading-relaxed font-medium">
                Access your real-time ledger, Make Bill Payments, Recharges , Fastag Recharge and much more in one unified platform.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              {[
                { label: "Bank-Grade Encryption & 2FA Access", icon: <ShieldCheck className="w-4 h-4 text-emerald-300" /> },
                { label: "Instant T+0 / Real-Time Settlements", icon: <Zap className="w-4 h-4 text-amber-300" /> },
                { label: "High Success Rate", icon: <CheckCircle2 className="w-4 h-4 text-sky-300" /> }
              ].map((feat, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-xs">
                  <div className="p-1 rounded-lg bg-white/10">{feat.icon}</div>
                  <span className="text-xs font-semibold text-slate-200">{feat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative z-10 pt-6 border-t border-white/10">
            <p className="text-[11px] text-slate-400 font-medium">
              © {new Date().getFullYear()} Shree Balajee Connect Pvt Ltd. All rights reserved.
            </p>
          </div>
        </div>

        {/* ================= RIGHT SECTION: INTERACTIVE FORM ================= */}
        <div className="lg:col-span-7 flex flex-col justify-center p-6 sm:p-10 lg:p-12 bg-white">
          <div className="w-full max-w-md mx-auto space-y-6">
            
            {/* Mobile Branding Header */}
            <div className="lg:hidden text-center pb-2">
              <img src={bingopayLogo} alt="BingoPay" className="h-10 w-auto mx-auto object-contain" />
            </div>

            {/* Error Notification Pill */}
            {errorMessage && (
              <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200/80 flex items-start gap-2.5 animate-in fade-in duration-150">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <p className="text-xs font-semibold text-rose-700 leading-snug">{errorMessage}</p>
              </div>
            )}

            {/* Progress message between the device steps (e.g. "MPIN set successfully") */}
            {noticeMessage && view === "DEVICE" && (
              <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200/80 flex items-start gap-2.5 animate-in fade-in duration-150">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-xs font-semibold text-emerald-700 leading-snug">{noticeMessage}</p>
              </div>
            )}

            {/* VIEW 1: LOGIN FORM */}
            {view === "LOGIN" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 tracking-tight"> Sign In</h2>
                  <p className="text-xs text-slate-500 mt-1">Enter your registered merchant credentials to access your dashboard.</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  {/* Mobile Input */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Mobile Number</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <Smartphone className="w-4 h-4" />
                      </div>
                      <input
                        type="tel"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                        maxLength={10}
                        placeholder="10-digit mobile number"
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 transition-all outline-hidden"
                      />
                    </div>
                  </div>

                  {/* Password Input */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Password</label>
                      <button
                        type="button"
                        onClick={triggerForgotPassword}
                        disabled={isLoading}
                        className="text-xs font-bold text-blue-600 hover:text-blue-700 transition"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <Lock className="w-4 h-4" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full pl-10 pr-11 py-3 bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 transition-all outline-hidden"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs font-bold rounded-xl shadow-md shadow-blue-500/20 transition-all active:scale-98 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Signing In...
                      </>
                    ) : (
                      <>
                        Sign In to Terminal <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* VIEW 2: OTP VERIFICATION */}
            {view === "OTP" && (
              <div className="space-y-6 animate-in fade-in duration-150">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Verify OTP</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Enter the 6-digit code sent to <span className="font-semibold text-slate-800 font-mono">{otpSentTo || `+91 ${mobile}`}</span>
                  </p>
                </div>

                <form onSubmit={handleOtpVerify} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Verification OTP</label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      maxLength={6}
                      placeholder="• • • • • •"
                      className="w-full py-3.5 bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl text-center text-xl font-bold tracking-[0.8em] text-slate-800 outline-hidden font-mono"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-md shadow-blue-500/20 transition active:scale-98 flex items-center justify-center gap-2"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify Code & Proceed"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setView("LOGIN")}
                    className="w-full py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition flex items-center justify-center gap-1.5"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
                  </button>
                </form>
              </div>
            )}

            {/* VIEW: DEVICE CHECK, MPIN SETUP AND MPIN CONFIRMATION */}
            {view === "DEVICE" && deviceChallenge && (() => {
              const stage = deviceChallenge.stage;
              if (stage === "CHOOSE_DEVICE" || stage === "CHOOSE_SESSION") {
                const choice = deviceChallenge.choice || {};
                const isDevice = stage === "CHOOSE_DEVICE";
                const rows = (isDevice ? choice.devices : choice.sessions) || [];
                const keyOf = (r) => (isDevice ? r.deviceId : r.sessionId);
                const label = (r) => [r.browser, r.os, r.deviceType].filter(Boolean).join(" · ") || "Unknown device";
                const when = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "-");
                // a time the user decides by: dark and highlighted, the text around it stays quiet
                const Time = ({ children }) => <span className="font-semibold text-slate-900 bg-amber-100 rounded px-1.5 py-0.5">{children}</span>;
                const picked = rows.find((r) => keyOf(r) === pickedChoice);
                return (
                  <div className="space-y-5 animate-in fade-in duration-150">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                        {isDevice ? "Remove a device to continue" : "Log out a session to continue"}
                      </h2>
                      <p className="text-xs text-slate-500 mt-1">
                        {isDevice
                          ? `You can keep ${choice.maxDevices ? `up to ${choice.maxDevices}` : "a limited number of"} trusted devices. Choose the one you no longer use. This device is added in its place and you are signed in.`
                          : `${choice.scope === "device" ? "This device can have" : "You can have"} ${choice.maxSessions ? `${choice.maxSessions} sessions` : "a limited number of sessions"} at a time. Choose the one you no longer need; you are then signed in.`}
                      </p>
                    </div>
                    <div className="space-y-2.5 max-h-[22rem] overflow-y-auto pr-1">
                      {rows.map((r) => (
                        <label key={keyOf(r)} className={`block cursor-pointer rounded-xl border p-3.5 transition ${pickedChoice === keyOf(r) ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600" : "border-slate-200 hover:bg-slate-50"}`}>
                          <div className="flex items-start gap-3">
                            <input type="radio" name="choice" className="mt-1" checked={pickedChoice === keyOf(r)} onChange={() => setPickedChoice(keyOf(r))} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className="text-sm font-bold text-slate-800">{label(r)}</span>
                                {isDevice ? (
                                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${r.activeSessions > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                                    {r.activeSessions > 0 ? `${r.activeSessions} active session${r.activeSessions === 1 ? "" : "s"}` : "No active sessions"}
                                  </span>
                                ) : choice.scope === "user" && r.onThisDevice ? (
                                  <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800">This device</span>
                                ) : null}
                              </div>
                              <div className="mt-1.5 text-[11px] text-slate-500 leading-6">
                                {isDevice ? (
                                  <>Last IP {r.lastIp || "-"} · last used <Time>{when(r.lastSeenAt)}</Time> · added <Time>{when(r.createdAt)}</Time></>
                                ) : (
                                  <>IP {r.ip || "-"} · signed in <Time>{fullTime(r.loginAt)}</Time> · last active <Time>{fullTime(r.lastActivityAt)}</Time> · session …{shortSessionId(r.sessionId)}</>
                                )}
                              </div>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    {isDevice && picked && picked.activeSessions > 0 && (
                      <p className="text-xs font-semibold text-amber-800">
                        Removing this device also logs out its {picked.activeSessions} active session{picked.activeSessions === 1 ? "" : "s"}.
                      </p>
                    )}
                    <button
                      type="button"
                      disabled={isLoading || pickedChoice === null}
                      onClick={handleChoose}
                      className="w-full py-3.5 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl transition disabled:opacity-50"
                    >
                      {isLoading ? "Please wait..." : isDevice ? "Remove this device and sign in" : "Log out this session and sign in"}
                    </button>
                    <button
                      type="button"
                      onClick={() => leaveDeviceVerification("")}
                      className="w-full py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition flex items-center justify-center gap-1.5"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
                    </button>
                  </div>
                );
              }
              const showOtp = stage === "DEVICE_OTP" || stage === "DEVICE_OTP_ONLY" || stage === "SETUP_OTP";
              const showMpin = stage === "DEVICE_OTP" || stage === "MPIN_SETUP" || stage === "MPIN_CONFIRM";
              const heading = {
                DEVICE_OTP: "Verify this device",
                DEVICE_OTP_ONLY: "Verify this device",
                MPIN_SETUP: "Set your MPIN",
                SETUP_OTP: "Confirm your new MPIN",
                MPIN_CONFIRM: "Enter your MPIN",
              }[stage];
              const hint = {
                DEVICE_OTP: `This is a new device for your account. We sent a 6-digit OTP to ${deviceChallenge.maskedMobile}. Enter it with your MPIN to continue.`,
                DEVICE_OTP_ONLY: `This is a new device for your account. We sent a 6-digit OTP to ${deviceChallenge.maskedMobile}. Enter it to continue.`,
                MPIN_SETUP: "Choose a 6-digit MPIN and enter it again to confirm.",
                SETUP_OTP: "Enter the OTP to save your new MPIN.",
                MPIN_CONFIRM: "Enter your 6-digit MPIN to continue.",
              }[stage];
              const submitLabel = {
                DEVICE_OTP: "Verify & Sign In",
                DEVICE_OTP_ONLY: "Verify OTP",
                MPIN_SETUP: "Set MPIN",
                SETUP_OTP: "Verify OTP",
                MPIN_CONFIRM: "Sign In",
              }[stage];
              const onSubmit = stage === "MPIN_SETUP" ? handleMpinSetup : stage === "SETUP_OTP" ? handleSetupOtp : handleDeviceVerify;
              const field = "w-full py-3.5 bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl text-center text-xl font-bold tracking-[0.8em] text-slate-800 outline-hidden font-mono";
              return (
                <div className="space-y-6 animate-in fade-in duration-150">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{heading}</h2>
                    <p className="text-xs text-slate-500 mt-1">{hint}</p>
                  </div>

                  <form onSubmit={onSubmit} className="space-y-4">
                    {showOtp && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Verification OTP</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          value={deviceOtp}
                          onChange={(e) => setDeviceOtp(e.target.value.replace(/\D/g, ""))}
                          maxLength={6}
                          placeholder="• • • • • •"
                          className={field}
                        />
                      </div>
                    )}
                    {showMpin && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          {stage === "MPIN_SETUP" ? "New MPIN" : "MPIN"}
                        </label>
                        <input
                          type="password"
                          inputMode="numeric"
                          autoComplete="off"
                          value={deviceMpin}
                          onChange={(e) => setDeviceMpin(e.target.value.replace(/\D/g, ""))}
                          maxLength={6}
                          placeholder="• • • • • •"
                          className={field}
                        />
                      </div>
                    )}
                    {stage === "MPIN_SETUP" && (
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Confirm MPIN</label>
                        <input
                          type="password"
                          inputMode="numeric"
                          autoComplete="off"
                          value={deviceMpinConfirm}
                          onChange={(e) => setDeviceMpinConfirm(e.target.value.replace(/\D/g, ""))}
                          maxLength={6}
                          placeholder="• • • • • •"
                          className={field}
                        />
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-md shadow-blue-500/20 transition active:scale-98 flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : submitLabel}
                    </button>

                    {showOtp && (
                      <button
                        type="button"
                        onClick={handleDeviceResend}
                        disabled={resendIn > 0}
                        className="w-full py-2 text-xs font-bold text-slate-500 hover:text-slate-800 disabled:text-slate-300 transition"
                      >
                        {resendIn > 0 ? `Resend OTP in ${resendIn}s` : "Resend OTP"}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => leaveDeviceVerification("")}
                      className="w-full py-2.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition flex items-center justify-center gap-1.5"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
                    </button>
                  </form>
                </div>
              );
            })()}

            {/* VIEW 3 & 4: RESET PASSWORD / MPIN */}
            {(view === "RESET_PASSWORD" || view === "RESET_MPIN") && (
              <div className="space-y-6 animate-in fade-in duration-150">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                    {view === "RESET_PASSWORD" ? "Set New Password" : "Set New MPIN"}
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    {view === "RESET_PASSWORD"
                      ? "Create a strong alphanumeric password to secure your terminal account."
                      : "Set a 6-digit numeric transaction MPIN for authorizing payment settlements."}
                  </p>
                </div>

                <form onSubmit={handleResetSubmit} className="space-y-4">
                  {view === "RESET_PASSWORD" ? (
                    <>
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-600">New Password</label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            className="w-full pl-4 pr-11 py-3 bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-800 outline-hidden"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                          >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Confirm Password</label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            className="w-full pl-4 pr-11 py-3 bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-800 outline-hidden"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-600">New 6-Digit MPIN</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                            <KeyRound className="w-4 h-4" />
                          </div>
                          <input
                            type="password"
                            value={mpin}
                            onChange={(e) => setMpin(e.target.value.replace(/\D/g, ""))}
                            maxLength={6}
                            placeholder="Enter 6-digit MPIN"
                            className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-800 tracking-widest font-mono outline-hidden"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Confirm MPIN</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                            <KeyRound className="w-4 h-4" />
                          </div>
                          <input
                            type="password"
                            value={confirmMpin}
                            onChange={(e) => setConfirmMpin(e.target.value.replace(/\D/g, ""))}
                            maxLength={6}
                            placeholder="Confirm 6-digit MPIN"
                            className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-800 tracking-widest font-mono outline-hidden"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-md shadow-emerald-500/20 transition active:scale-98 flex items-center justify-center gap-2"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Complete"}
                  </button>
                </form>
              </div>
            )}

            {/* Google Play Store Badge */}
            <div className="pt-6 border-t border-slate-100 flex flex-col items-center justify-center gap-2">
              <span className="text-[11px] font-medium text-slate-400">Download the Android app</span>
              <a
                href="https://play.google.com/store/apps/details?id=com.bingopays"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 px-4 py-2 bg-slate-900 hover:bg-slate-800 transition rounded-xl text-white shadow-xs group active:scale-98"
              >
                <img src={playstore} alt="Google Play" className="h-5 w-auto object-contain" />
                <div className="text-left">
                  <p className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">Get it on</p>
                  <p className="text-xs font-bold leading-none mt-1 group-hover:text-sky-300 transition-colors">Google Play</p>
                </div>
              </a>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}

export default AuthContainer;
